import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { modeFromFlags, nextFlags, type FormFlag } from "../features/managedServices/Forms/formMode";
import {
    getFormsPage,
    patchFormMode as patchFormModeApi,
} from "../features/managedServices/services/managedServiceApi";
import type { MsForm, MsFormsPage, MsFormsQuery } from "../features/managedServices/types";
import { redirectToLoginPage } from "../shared/sessionUtility";
import { AppDispatch, RootState } from "./types";

export const DEFAULT_FORMS_SORT: [string, boolean] = ["id", true];

export interface MsFormsState {
    isLoading: boolean;
    error: string | null;
    forms: MsForm[];
    total: number;
    page: number;
    pageSize: number;
    formCode: string;
    formName: string;
    stateCode: string;
    formPack: string;
    sortColumn: [string, boolean];
}

const DEFAULT_PAGE_SIZE = 25;

const initialState: MsFormsState = {
    isLoading: false,
    error: null,
    forms: [],
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    formCode: "",
    formName: "",
    stateCode: "",
    formPack: "",
    sortColumn: DEFAULT_FORMS_SORT,
};

const msFormsSlice = createSlice({
    name: "managedServiceForms",
    initialState,
    reducers: {
        setLoading: (state: MsFormsState, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state: MsFormsState, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setFormsPage: (state: MsFormsState, action: PayloadAction<MsFormsPage>) => {
            state.forms = action.payload.items;
            state.total = action.payload.total;
            state.page = action.payload.page;
            state.pageSize = action.payload.pageSize;
        },
        setFormsQuery: (state: MsFormsState, action: PayloadAction<MsFormsQuery>) => {
            state.page = action.payload.page;
            state.pageSize = action.payload.pageSize;
            state.formCode = action.payload.formCode;
            state.formName = action.payload.formName;
            state.stateCode = action.payload.stateCode;
            state.formPack = action.payload.formPack;
            state.sortColumn = action.payload.sortColumn;
        },
        patchForm: (state: MsFormsState, action: PayloadAction<Partial<MsForm> & { id: number }>) => {
            const target = state.forms.find(form => form.id === action.payload.id);
            if (target) {
                Object.assign(target, action.payload);
            }
        },
    },
});

export const { setLoading, setError, setFormsPage, setFormsQuery, patchForm } =
    msFormsSlice.actions;

export const selectMsForms = (state: RootState): MsFormsState => state.managedServiceForms;

const reportApiError = (dispatch: AppDispatch, error: unknown, fallback: string): boolean => {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 401) {
        redirectToLoginPage();
        return true;
    }
    dispatch(
        setError(
            status === 403
                ? "You do not have access to Managed Service (an internal/TSA role is required)."
                : fallback
        )
    );
    return false;
};

export const loadForms =
    (query: Partial<MsFormsQuery> = {}) =>
    async (dispatch: AppDispatch): Promise<void> => {
        const next: MsFormsQuery = {
            page: query.page ?? 1,
            pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
            formCode: query.formCode ?? "",
            formName: query.formName ?? "",
            stateCode: query.stateCode ?? "",
            formPack: query.formPack ?? "",
            sortColumn: query.sortColumn ?? DEFAULT_FORMS_SORT,
        };
        dispatch(setLoading(true));
        dispatch(setError(null));
        dispatch(setFormsQuery(next));
        try {
            dispatch(setFormsPage(await getFormsPage(next)));
        } catch (error) {
            reportApiError(dispatch, error, "Unable to load enabled forms. Please try again.");
            // eslint-disable-next-line no-console
            console.error("loadForms failed:", error);
        } finally {
            dispatch(setLoading(false));
        }
    };

const reloadForms = (dispatch: AppDispatch, getState: () => RootState): void => {
    const { page, pageSize, formCode, formName, stateCode, formPack, sortColumn } =
        selectMsForms(getState());
    dispatch(loadForms({ page, pageSize, formCode, formName, stateCode, formPack, sortColumn }));
};

/** Per-form PATCH generation so a slower in-flight toggle cannot overwrite a newer one. */
const formFlagPatchGeneration = new Map<number, number>();
/** One click can emit two SToggle events; drop the second until the PATCH finishes. */
const formFlagPatchInFlight = new Set<number>();

export const toggleFormFlag =
    (formId: number, flag: FormFlag, enabled: boolean) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        const form = selectMsForms(getState()).forms.find(row => row.id === formId);
        if (!form || form.state === "defining" || formFlagPatchInFlight.has(formId)) {
            return;
        }
        formFlagPatchInFlight.add(formId);
        const next = nextFlags(form, flag, enabled);
        const mode = modeFromFlags(next);
        const generation = (formFlagPatchGeneration.get(formId) ?? 0) + 1;
        formFlagPatchGeneration.set(formId, generation);
        dispatch(
            patchForm({
                id: formId,
                ...next,
                runUntil: mode.runUntil,
                ecmWriteMode: mode.ecmWriteMode,
            })
        );
        try {
            const result = await patchFormModeApi(formId, mode.runUntil, mode.ecmWriteMode);
            if (formFlagPatchGeneration.get(formId) !== generation) {
                return;
            }
            dispatch(patchForm(result));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("toggleFormFlag failed:", error);
            if (formFlagPatchGeneration.get(formId) !== generation) {
                return;
            }
            if (!reportApiError(dispatch, error, "Could not update the form flags.")) {
                reloadForms(dispatch, getState);
            }
        } finally {
            formFlagPatchInFlight.delete(formId);
        }
    };

export default msFormsSlice.reducer;
